/**
 * Rich feedback from AI speaking grade API (criteria, transcript, suggestions).
 */

import * as React from 'react';
import map from 'lodash/map';
import compact from 'lodash/compact';
import {
    cn,
    Button,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    tiptapJsonToPlainText,
    isTiptapDocument,
} from '@ai-content-hub/content-kit';
import { ChevronDown } from 'lucide-react';
import type { SpeakingItem } from './schema';

export interface SpeakingAiGradePanelProps {
    details: Record<string, unknown>;
    items: SpeakingItem[];
    className?: string;
    t: (key: string, fallback?: string) => string;
}

function humanizeCriterionKey(key: string): string {
    return map(key.split('_'), (w) =>
        w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''
    ).join(' ');
}

function isNonEmptyStringList(v: unknown): v is string[] {
    return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function SpeakingAiGradeBlock({
    r,
    heading,
    blockKey,
    t,
}: {
    r: Record<string, unknown>;
    heading: string;
    blockKey: string;
    t: (key: string, fallback?: string) => string;
}): React.ReactElement {
    const [detailsOpen, setDetailsOpen] = React.useState(false);

    const overall =
        typeof r.overall_score === 'number' && Number.isFinite(r.overall_score)
            ? r.overall_score
            : null;
    const wordCount =
        typeof r.word_count === 'number' && Number.isFinite(r.word_count) ? r.word_count : null;

    const criteriaRaw = r.criteria_scores;
    const criteriaObj =
        criteriaRaw && typeof criteriaRaw === 'object' && !Array.isArray(criteriaRaw)
            ? (criteriaRaw as Record<string, unknown>)
            : null;

    const feedbackRaw = r.detailed_feedback;
    const feedback =
        feedbackRaw && typeof feedbackRaw === 'object' && !Array.isArray(feedbackRaw)
            ? (feedbackRaw as Record<string, unknown>)
            : null;

    const strengths = feedback?.strengths;
    const weaknesses = feedback?.weaknesses;
    const corrections = feedback?.corrections;

    const suggested =
        typeof r.suggested_revision === 'string' ? r.suggested_revision.trim() : '';
    const transcript = typeof r.transcript === 'string' ? r.transcript.trim() : '';

    const criterionEntries = criteriaObj ? Object.entries(criteriaObj) : [];

    const hasStrengths = isNonEmptyStringList(strengths) && strengths.length > 0;
    const hasWeaknesses = isNonEmptyStringList(weaknesses) && weaknesses.length > 0;
    const hasCorrections = isNonEmptyStringList(corrections) && corrections.length > 0;

    const hasExpandableSections =
        criterionEntries.length > 0 ||
        hasStrengths ||
        hasWeaknesses ||
        hasCorrections ||
        Boolean(suggested) ||
        Boolean(transcript);

    const expandableBody = (
        <div className="speaking-player-ai-grade-details-inner space-y-4 pt-2">
            {criterionEntries.length > 0 ? (
                <div className="speaking-player-ai-grade-criteria space-y-2">
                    <p className="text-sm font-medium text-foreground">{t('aiGradingCriteria', 'Criteria')}</p>
                    <ul className="space-y-2 list-none p-0 m-0">
                        {map(criterionEntries, ([ckey, raw]) => {
                            const c =
                                raw && typeof raw === 'object' && !Array.isArray(raw)
                                    ? (raw as Record<string, unknown>)
                                    : null;
                            const sc =
                                typeof c?.score === 'number' && Number.isFinite(c.score)
                                    ? c.score
                                    : null;
                            const mx =
                                typeof c?.max === 'number' && Number.isFinite(c.max) ? c.max : 100;
                            const desc =
                                typeof c?.description === 'string' ? c.description.trim() : '';
                            return (
                                <li
                                    key={`${blockKey}-${ckey}`}
                                    className="speaking-player-ai-grade-criterion rounded-sm bg-muted/30 p-2 border border-border"
                                >
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                        <span className="text-sm font-medium text-foreground">
                                            {humanizeCriterionKey(ckey)}
                                        </span>
                                        {sc != null ? (
                                            <span className="text-sm tabular-nums text-muted-foreground">
                                                <span className="font-semibold text-foreground">
                                                    {sc}
                                                </span>
                                                <span> / {mx}</span>
                                            </span>
                                        ) : null}
                                    </div>
                                    {desc ? (
                                        <p className="speaking-player-ai-grade-criterion-desc mt-1 text-xs text-muted-foreground leading-relaxed">
                                            {desc}
                                        </p>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}

            {feedback ? (
                <div className="speaking-player-ai-grade-detailed space-y-3">
                    {hasStrengths ? (
                        <div>
                            <p className="text-sm font-medium text-success mb-1">{t('aiGradingStrengths', 'Strengths')}</p>
                            <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
                                {map(strengths, (s, i) => (
                                    <li key={`${blockKey}-st-${i}`}>{s}</li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    {hasWeaknesses ? (
                        <div>
                            <p className="text-sm font-medium text-warning mb-1">{t('aiGradingWeaknesses', 'Weaknesses')}</p>
                            <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
                                {map(weaknesses, (s, i) => (
                                    <li key={`${blockKey}-wk-${i}`}>{s}</li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    {hasCorrections ? (
                        <div>
                            <p className="text-sm font-medium text-info mb-1">{t('aiGradingCorrections', 'Corrections')}</p>
                            <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
                                {map(corrections, (s, i) => (
                                    <li key={`${blockKey}-cr-${i}`}>{s}</li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {suggested ? (
                <div className="speaking-player-ai-grade-suggestion rounded-sm bg-info/10 border border-border p-3">
                    <p className="text-xs font-medium text-foreground mb-1">{t('aiGradingSuggestedRevision', 'Suggested revision')}</p>
                    <p className="text-sm text-foreground leading-relaxed">{suggested}</p>
                </div>
            ) : null}

            {transcript ? (
                <div className="speaking-player-ai-grade-transcript">
                    <p className="text-xs font-medium text-muted-foreground mb-1">{t('aiGradingTranscript', 'Transcript')}</p>
                    <blockquote className="speaking-player-ai-grade-transcript-quote text-sm border-l-2 border-primary pl-3 text-foreground italic">
                        {transcript}
                    </blockquote>
                </div>
            ) : null}
        </div>
    );

    return (
        <div className={cn('speaking-player-ai-grade-block space-y-4')}>
            <h4 className="speaking-player-ai-grade-block-title text-base font-semibold text-foreground">
                {heading}
            </h4>

            {(overall != null || wordCount != null) && (
                <div className="speaking-player-ai-grade-summary flex flex-wrap gap-4 text-sm">
                    {overall != null ? (
                        <div className="speaking-player-ai-grade-overall rounded-sm bg-muted/30 px-3 py-2 border border-border">
                            <span className="text-muted-foreground">{t('aiGradingOverallScore', 'Overall score')}</span>{' '}
                            <span className="font-semibold text-foreground tabular-nums">
                                {overall}
                            </span>
                            <span className="text-muted-foreground"> / 100</span>
                        </div>
                    ) : null}
                    {wordCount != null ? (
                        <div className="speaking-player-ai-grade-wordcount rounded-sm bg-muted/30 px-3 py-2 border border-border">
                            <span className="text-muted-foreground">{t('aiGradingWordCount', 'Word count')}</span>{' '}
                            <span className="font-semibold text-foreground tabular-nums">
                                {wordCount}
                            </span>
                        </div>
                    ) : null}
                </div>
            )}

            {hasExpandableSections ? (
                <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
                    <CollapsibleTrigger asChild>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="speaking-player-ai-grade-details-trigger cursor-pointer gap-1.5 text-foreground border-border shadow-none hover:bg-muted/40"
                        >
                            <span>{detailsOpen ? t('aiGradingHideDetails', 'Hide details') : t('aiGradingMoreDetails', 'More details')}</span>
                            <ChevronDown
                                className={cn(
                                    'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                                    detailsOpen && 'rotate-180'
                                )}
                                aria-hidden
                            />
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                        {expandableBody}
                    </CollapsibleContent>
                </Collapsible>
            ) : null}
        </div>
    );
}

export function SpeakingAiGradePanel({
    details,
    items,
    className,
    t,
}: SpeakingAiGradePanelProps): React.ReactElement | null {
    if (details.aiGrading !== true) {
        return null;
    }
    const raw = details.apiResponses;
    if (!Array.isArray(raw) || raw.length === 0) {
        return null;
    }

    const titleById = new Map(map(items, (i) => {
        const t = i.data?.title;
        if (t && typeof t === 'object' && isTiptapDocument(t)) {
            return [i.id, tiptapJsonToPlainText(t).trim()] as const;
        }
        return [i.id, (typeof t === 'string' ? t : '').trim()] as const;
    }));

    return (
        <div
            className={cn(
                'speaking-player-ai-grade border-0 shadow-sm rounded-xl bg-card p-4 md:p-6 space-y-8',
                className
            )}
        >
            {compact(
                map(raw, (entry: unknown, idx: number) => {
                    const rec = entry as { itemId?: string; result?: unknown };
                    const itemId = typeof rec.itemId === 'string' ? rec.itemId : '';
                    const titleHint = itemId ? titleById.get(itemId) : '';
                    const heading =
                        raw.length > 1
                            ? `${idx + 1}. ${titleHint || t('aiGradingPrompt', 'Speaking prompt')}`
                            : t('aiGradingResults', 'AI speaking results');
                    const res =
                        rec.result && typeof rec.result === 'object' && !Array.isArray(rec.result)
                            ? (rec.result as Record<string, unknown>)
                            : null;
                    if (!res) {
                        return null;
                    }
                    return (
                        <SpeakingAiGradeBlock
                            key={`${itemId || 'r'}-${idx}`}
                            r={res}
                            heading={heading}
                            blockKey={`${itemId || 'r'}-${idx}`}
                            t={t}
                        />
                    );
                })
            )}
        </div>
    );
}
